import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Box } from "@mui/material";
import moment from "moment";

import { ActionButton, CardLayout, InputField, SelectField } from "../v3";
import {
  AccountService,
  AdminService,
  FinanceService,
  HumanResourceService,
  ProjectService,
  PurchaseService,
} from "../../services";
import { get_date } from "../generalComponets";
import { editTransactionValidationRules, validateAdvancePayment } from "../../pages/transaction/validation";
import { useHasPermission } from "../../utils";
import {
  ENABLE_EMPLOYEE_SALARY_BACKDATE_ENTRY,
  ENABLE_WORKER_PAYMENT_BACKDATE_ENTRY,
  ENABLE_SUBCONTRACTOR_TRANSACTION_BACKDATE_ENTRY,
  ENABLE_VENDOR_PAYMENT_BACKDATE_ENTRY,
  ENABLE_USER_PAYMENT_BACKDATE_ENTRY,
  ENABLE_ACCOUNT_PAYMENT_BACKDATE_ENTRY,
  VIEW_ACCOUNT,
  ENABLE_WORKER_ADVANCE_PAYMENT,
  ENABLE_VENDOR_ADVANCE_PAYMENT,
  ENABLE_SUBCONTRACTOR_ADVANCE_PAYMENT,
} from "../../constants";
import { useSelector } from "react-redux";
import { renameCashAccount } from "../../utils/accountUtils";

export const EditTransactionForm = ({ transactionId }: { transactionId: any }) => {
  const [transactionDetails, setTransactionDetails] = useState<any>({
    toAccountId: undefined,
    amount: undefined,
    description: undefined,
    projectId: undefined,
    projectStageId: undefined,
    fromAccountId: undefined,
  });
  const [toAccount, setToAccount] = useState<any>([]);
  const [errors, setErrors] = useState<any>({});
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedProjectStage, setSelectedProjectStage] = useState<any>(null);
  const [stageIds, setStageIds] = useState<any>([]);
  const [projectStages, setProjectStages] = useState<any[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [destinationBalance, setDestinationBalance] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const workerId = searchParams.get("workerId");
  const employeeId = searchParams.get("employeeId");
  const vendorId = searchParams.get("vendorId");
  const subcontractorId = searchParams.get("subcontractorId");
  const userId = searchParams.get("userId");
  const accountId = searchParams.get("accountId");

  const backdatePermissionMap = {
    WORKER: ENABLE_WORKER_PAYMENT_BACKDATE_ENTRY,
    EMPLOYEE: ENABLE_EMPLOYEE_SALARY_BACKDATE_ENTRY,
    VENDOR: ENABLE_VENDOR_PAYMENT_BACKDATE_ENTRY,
    SUBCONTRACTOR: ENABLE_SUBCONTRACTOR_TRANSACTION_BACKDATE_ENTRY,
    USER: ENABLE_USER_PAYMENT_BACKDATE_ENTRY,
    ACCOUNT: ENABLE_ACCOUNT_PAYMENT_BACKDATE_ENTRY,
  };

  const advancePaymentPermissionMap = {
    WORKER: ENABLE_WORKER_ADVANCE_PAYMENT,
    VENDOR: ENABLE_VENDOR_ADVANCE_PAYMENT,
    SUBCONTRACTOR: ENABLE_SUBCONTRACTOR_ADVANCE_PAYMENT,
  };

  const currentType = workerId
    ? "WORKER"
    : employeeId
      ? "EMPLOYEE"
      : vendorId
        ? "VENDOR"
        : subcontractorId
          ? "SUBCONTRACTOR"
          : userId
            ? "USER"
            : accountId
              ? "ACCOUNT"
              : null;

  const currentBackdatePermission = currentType ? backdatePermissionMap[currentType] : null;
  const advancePaymentPermission = currentType ? advancePaymentPermissionMap[currentType] : null;

  const enableBackDate = useHasPermission(currentBackdatePermission);
  const enableAdvancePayment = useHasPermission(advancePaymentPermission);
  const hasViewAccount = useHasPermission(VIEW_ACCOUNT);

  const { user } = useSelector((state: any) => state.user);
  const { ownerUserId: companyAdminId } = user.company;

  useEffect(() => {
    fetchData();
  }, [transactionId]);

  useEffect(() => {
    if (workerId) {
      fetchToAccountDetails("WORKER", workerId);
    } else if (employeeId) {
      fetchToAccountDetails("EMPLOYEE", employeeId);
    } else if (vendorId) {
      fetchToAccountDetails("VENDOR", vendorId);
    } else if (subcontractorId) {
      fetchToAccountDetails("SUBCONTRACTOR", subcontractorId);
    } else if (userId) {
      fetchToAccountDetails("USER", userId);
    } else if (accountId) {
      fetchToAccountDetails("ACCOUNT", accountId);
    }
  }, [workerId, employeeId, vendorId, subcontractorId, userId, accountId]);

  const stageFilter = useMemo(
    () => ({
      ids: stageIds,
    }),
    [stageIds],
  );

  const fetchData = async () => {
    try {
      if (subcontractorId) {
        const { data: expense } = await FinanceService.getExpense(transactionId);
        setTransactionDetails({
          toAccountId: expense.destinationId,
          amount: Number(expense.amount),
          description: expense.description,
          date: get_date(expense.date, "YYYY-MM-DD"),
          projectId: expense.projectId,
          categoryId: expense.categoryId,
        });
        setSelectedProject(expense?.project);
        // fetch project stages
        if (expense?.projectId) fetchProjectStages(expense?.projectId);
        // fetch stages
        if (expense?.projectStageId) fetchProjectStageById(expense?.projectStage?.stageId);
      } else {
        const { data: transaction } = await FinanceService.getTransactionById(transactionId);

        if (transaction?.fromAccountId) {
          const { data: fromAccount } = await AccountService.getAccountById(transaction?.fromAccountId);
          const renamedAccount = renameCashAccount(fromAccount, user, companyAdminId);
          setSelectedAccount(renamedAccount);
        }

        setSelectedProject(transaction.project);
        setTransactionDetails({
          toAccountId: transaction.toAccountId,
          amount: Number(transaction.amount),
          description: transaction.description,
          date: get_date(transaction.date, "YYYY-MM-DD"),
          projectId: transaction.projectId,
          fromAccountId: transaction.fromAccountId,
        });
        // fetch project stages
        if (transaction?.projectId) fetchProjectStages(transaction?.projectId);
        // fetch stages
        if (transaction?.projectStageId) fetchProjectStageById(transaction?.projectStage?.stageId);
      }
    } catch (error) {
      console.error(`🚀 ~ fetchData ~ error:`, error);
      toast.error("error fetching transaction details");
    }
  };

  const fetchProjectStages = async (projectId) => {
    try {
      const { data: first } = await ProjectService.getProjectStages({
        projectId,
        page: 1,
        limit: 10,
      });

      const { data } = await ProjectService.getProjectStages({
        projectId,
        page: 1,
        limit: first.count,
      });

      const stages = [...new Set(data.rows.map((item) => item.stageId))];

      setStageIds(stages.join(","));
      setProjectStages(data.rows);
    } catch (error) {
      console.error("🚀 ~ fetchProjectStages ~ error:", error);
    }
  };

  const fetchProjectStageById = async (stageId: any) => {
    try {
      const { data: stage } = await ProjectService.getStageById(stageId);
      setSelectedProjectStage(stage);
    } catch (error) {
      toast.error("Failed to fetch stage details");
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };
  // fetch account details to show in the form
  const fetchToAccountDetails = async (type: string, accountId: any) => {
    try {
      let data: any = [];

      switch (type) {
        case "WORKER":
          const worker = await HumanResourceService.getEmployeeById(accountId, { includeDeleted: true });
          data = worker?.data;
          break;
        case "EMPLOYEE":
          const employee = await HumanResourceService.getEmployeeById(accountId, { includeDeleted: true });
          data = employee?.data;
          break;
        case "VENDOR":
          const vendor = await PurchaseService.getVendorById(accountId, { includeDeleted: true });
          data = vendor?.data;
          break;
        case "SUBCONTRACTOR":
          const subcontractor = await PurchaseService.getVendorById(accountId, { includeDeleted: true });
          data = subcontractor?.data;
          break;
        case "USER":
          const userResponse = await AdminService.getUserDetails(accountId);
          data = userResponse?.data;
          break;
        case "ACCOUNT":
          const account = await AccountService.getAccountById(accountId);
          data = renameCashAccount(account?.data, user, companyAdminId);
          break;
        default:
          break;
      }

      setToAccount(data);
      if (["WORKER", "VENDOR", "SUBCONTRACTOR"].includes(type)) {
        setDestinationBalance(invertBalance(data?.account?.balance));
      }
    } catch (error) {
      console.error(`🚀 ~ fetchToAccountDetails ~ error:`, error);
      toast.error("error fetching account details");
    }
  };

  const invertBalance = (balance: any) => -(Number(balance) || 0);

  const getAmountLabel = ({ enableAdvancePayment, destinationType, destinationBalance }: any) => {
    if (!enableAdvancePayment && ["WORKER", "VENDOR", "SUBCONTRACTOR"].includes(destinationType) && destinationBalance !== null) {
      return `Amount (Balance: ${Number(destinationBalance).toLocaleString()})`;
    }

    return "Amount";
  };

  const handleProjectChange = (selected: any) => {
    setStageIds([]);
    setSelectedProjectStage(null);
    setSelectedProject(selected);
    setTransactionDetails((prev: any) => ({ ...prev, projectId: selected?.id || null, projectStageId: null }));
    if (selected?.id) fetchProjectStages(selected.id);
  };

  const handleProjectStageChange = (selected: any) => {
    const projectStage = projectStages.find((item: any) => item?.stageId === selected?.id);
    setSelectedProjectStage(selected);
    setTransactionDetails((prev: any) => ({ ...prev, projectStageId: projectStage?.id || null }));
  };

  const handleAccountChange = (selected: any) => {
    setSelectedAccount(selected);
    setTransactionDetails((prev: any) => ({ ...prev, fromAccountId: selected?.id || null }));
  };

  const updateDetailsState = (event: any) => {
    const { name, value } = event.target;
    setTransactionDetails((prev: any) => ({ ...prev, [name]: value }));

    if (name === "amount") {
      let amountError;

      if (value !== "" && Number(value) < 1) {
        amountError = "Amount must be greater than 0";
      } else {
        amountError = validateAdvancePayment({
          amount: value,
          destinationType: currentType,
          destinationBalance,
          enableAdvancePayment,
        });
      }

      setErrors((prev: any) => {
        const nextErrors = { ...prev };

        if (amountError) nextErrors.amount = amountError;
        else delete nextErrors.amount;

        return nextErrors;
      });
    }
  };

  const handleClearForm = () => {
    setTransactionDetails({
      toAccountId: undefined,
      amount: undefined,
      description: undefined,
      projectId: undefined,
      projectStageId: undefined,
      fromAccountId: undefined,
    });
    setSelectedProject(null);
    setSelectedProjectStage(null);
    setSelectedAccount(null);
    setErrors({});
    setDestinationBalance(null);
  };

  const handleCancelClick = () => {
    setErrors({});
    handleClearForm();
    navigate(-1);
  };

  const getToAccountDisplayName = (toAccount: any) => {
    if (!toAccount) return "";
    if (toAccount.name) return toAccount.name;
    if (toAccount.firstName || toAccount.lastName) {
      return `${toAccount.firstName ?? ""} ${toAccount.lastName ?? ""}`.trim();
    }
    return "";
  };

  const handleUpdateTransaction = async () => {
    if (loading) return;
    const payload = {
      ...transactionDetails,
      amount: Number(transactionDetails.amount),
      date: moment(`${transactionDetails.date} ${moment().format("HH:mm:ss")}`).toISOString(),
    };

    setErrors({});
    const validationErrors = editTransactionValidationRules({ ...payload, subcontractorId }, currentType, {
      destinationBalance,
      enableAdvancePayment,
    });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const editExpensePayload = {
      amount: payload.amount,
      date: payload.date,
      description: payload.description,
      projectId: payload.projectId,
      projectStageId: payload.projectStageId,
      categoryId: payload.categoryId,
    };

    try {
      setLoading(true);
      if (subcontractorId) await FinanceService.updateExpense(transactionId, editExpensePayload);
      else await FinanceService.updateTransaction(transactionId, payload);
      toast.success(`${subcontractorId ? "Expense" : "Transaction"} updated successfully`);
      handleCancelClick();
    } catch (error) {
      let errorMessage = `Error updating ${subcontractorId ? "expense" : "transaction"}`;
      const response = error?.response?.data;
      if (Array.isArray(response?.message)) {
        errorMessage = response.message.join(", ");
      } else if (typeof response?.message === "string") {
        errorMessage = response.message;
      }

      toast.error(errorMessage);
      console.error(`🚀 ~ handleUpdateTransaction ~ error:`, error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <CardLayout>
      <Box display="flex" gap={2}>
        <InputField
          disabled={true} // can't be edited
          required
          label="To Account"
          name="toAccountId"
          value={getToAccountDisplayName(toAccount)}
          error={!!errors.toAccountId}
          helperText={errors.toAccountId}
          fullWidth
        />
        {!subcontractorId && (
          <SelectField
            label="From Account"
            value={selectedAccount}
            getData={AccountService.getAdminAndUserAccounts}
            getDataStaticFilters={{ hasViewAccount, user, companyAdminId }}
            required
            error={!!errors.fromAccountId}
            helperText={errors.fromAccountId}
            onChange={handleAccountChange}
            style={{ width: "100%" }}
          />
        )}
        <InputField
          required
          label={getAmountLabel({
            enableAdvancePayment,
            destinationType: currentType,
            destinationBalance,
          })}
          name="amount"
          type="number"
          value={transactionDetails.amount}
          onChange={updateDetailsState}
          error={!!errors.amount}
          helperText={errors.amount}
          fullWidth
        />
      </Box>

      <Box display="flex" gap={2}>
        <InputField
          required
          label="Date"
          name="date"
          type="date"
          value={transactionDetails.date}
          onChange={updateDetailsState}
          error={!!errors.date}
          helperText={errors.date}
          fullWidth
          inputSlotProps={{
            htmlInput: {
              min: enableBackDate ? undefined : get_date(Date.now(), "YYYY-MM-DD"),
              onKeyDown: enableBackDate ? undefined : (e) => e.preventDefault(),
            },
          }}
        />
        <SelectField
          label="Project"
          value={selectedProject}
          getData={ProjectService.getProjectList}
          getDataStaticFilters={{ status: ["TODO", "INPROGRESS"] }}
          required={currentType === "SUBCONTRACTOR"}
          error={!!errors.projectId}
          helperText={errors.projectId}
          onChange={handleProjectChange}
          style={{ width: "100%" }}
        />
        <SelectField
          label="Stage"
          value={selectedProjectStage}
          getData={ProjectService.getStages}
          getDataStaticFilters={stageFilter}
          disabled={stageIds.length === 0}
          error={!!errors.projectStageId}
          helperText={errors.projectStageId}
          onChange={handleProjectStageChange}
          style={{ width: "100%" }}
        />
      </Box>
      <Box display="flex" gap={2}>
        <InputField
          label="Description"
          name="description"
          value={transactionDetails.description}
          onChange={updateDetailsState}
          error={!!errors.description}
          helperText={errors.description}
          fullWidth
          autoComplete="on"
        />
      </Box>
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <ActionButton buttonText="cancel" variant="outlined" onButtonClick={handleCancelClick} />
        <ActionButton buttonText="save" onButtonClick={handleUpdateTransaction} loading={loading} />
      </Box>
    </CardLayout>
  );
};
